import { api, el, toast } from "../ui/dom";
import { icon } from "../ui/icons";
import { openModal, type Modal } from "../ui/modal";
import { uploadControl } from "../ui/upload";

// My profile (brief 1E-5, mockup v57 avatar menu › My profile): anyone signed in, the owner
// included, sets their own name, mobile, email, professional title and specialty (brief 1E-7),
// license number, expiry and card, "I also make visits" and a typed signature. The title decides what they can sign (the company's
// setting in Settings › Home care). Saving is in the history (PUT /api/me/profile).

interface Profile {
  personId: string | null;
  name: string;
  mobile: string;
  email: string;
  title: string | null;
  titles: { value: string | null; label: string }[];
  licenseNumber: string;
  licenseExpires: string | null;
  licenseCard: { id: string; name: string } | null;
  licenseStatus: string | null;
  license: { flag: "expired" | "expiring" | null; words: string };
  makesVisits: boolean;
  signature: string;
  /** Brief 1E-7: Specialty, more than one. */
  specialties: string[];
}

let seq = 0;
const field = (label: string, input: HTMLElement, hint?: HTMLElement) => {
  const id = `mp-${++seq}`;
  input.id = id;
  return el("div", { class: "field" }, el("label", { for: id }, label), input, hint ?? null);
};

/** Opens My profile; `onSaved` hears the saved name (the top bar shows it). */
export async function openMyProfile(onSaved?: (name: string) => void): Promise<void> {
  const res = await api<Profile>("GET", "/api/me/profile");
  if (!res.ok) return toast("My profile isn't available", res.body.error);
  sheet(res.body, onSaved);
}

function sheet(p: Profile, onSaved?: (name: string) => void): Modal {
  const name = el("input", { type: "text", value: p.name, autocomplete: "name" });
  const mobile = el("input", {
    type: "text",
    inputMode: "tel",
    value: p.mobile ?? "",
    autocomplete: "tel",
  });
  const email = el("input", { type: "text", value: p.email ?? "", autocomplete: "email" });
  const title = el(
    "select",
    {},
    ...p.titles.map((t) =>
      el("option", { value: t.value ?? "", selected: (t.value ?? null) === p.title }, t.label),
    ),
  );
  const license = el("input", { type: "text", value: p.licenseNumber });
  // Specialty (brief 1E-7, mockup v59): under the professional title, more than one.
  const specHint = el(
    "span",
    { class: "small muted", id: `mp-sph-${++seq}` },
    "For example ICU, pediatrics, home health. More than one is fine.",
  );
  const specialty = el("input", {
    type: "text",
    value: p.specialties.join(", "),
    "aria-describedby": specHint.id,
  });
  const expires = el("input", { type: "date", value: p.licenseExpires ?? "" });
  const visitsName = `mp-v-${++seq}`;
  const yes = el("input", { type: "radio", name: visitsName, checked: p.makesVisits });
  const no = el("input", { type: "radio", name: visitsName, checked: !p.makesVisits });
  const sigHint = el(
    "span",
    { class: "small muted", id: `mp-sh-${seq}` },
    "Typed signature used when signing. Draw one instead on the phone.",
  );
  const signature = el("input", {
    type: "text",
    value: p.signature,
    "aria-describedby": sigHint.id,
  });
  let cardId: string | null = null;
  const cardSaid = el(
    "span",
    { class: "small muted" },
    p.licenseCard
      ? `${p.licenseCard.name}${p.licenseStatus === "submitted" ? " · in review" : ""}`
      : "",
  );
  // The card goes on your own record, so it needs your profile saved once first.
  const card = p.personId
    ? uploadControl({
        kind: "person",
        id: p.personId,
        label: "Upload",
        camera: true,
        onUploaded: (f) => {
          cardId = f.id;
          cardSaid.textContent = `${f.name} · saved with the profile`;
        },
      }).el
    : (() => {
        const b = el("button", { type: "button", class: "btn sm" }, icon("upload"), " Upload");
        b.addEventListener("click", async () => {
          const saved = await save(false);
          if (saved) {
            m.close();
            sheet(saved, onSaved);
            toast("Profile saved", "Now pick the license card");
          }
        });
        return b;
      })();
  const flag = p.license.flag
    ? el(
        "p",
        { class: `small ${p.license.flag === "expired" ? "bad" : "warn"}`, role: "status" },
        icon("alert"),
        ` ${p.license.words}. Flagged for renewal; office work goes on.`,
      )
    : null;

  const saveBtn = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
  const body = [
    el(
      "form",
      { class: "form myprof", "data-noop": "1" },
      el("div", { class: "grid2", style: "gap:8px" }, field("Name", name), field("Mobile", mobile)),
      field("Email", email),
      el(
        "div",
        { class: "grid2", style: "gap:8px" },
        field("Professional title (Maryland Board)", title),
        field("License number", license),
      ),
      field("Specialty", specialty, specHint),
      el(
        "div",
        { class: "grid2", style: "gap:8px" },
        field("License expires", expires),
        el("div", { class: "field" }, el("label", {}, "License card"), card, cardSaid),
      ),
      flag,
      el(
        "div",
        { class: "qrow" },
        el("span", {}, "I also make visits (puts me in Caregivers and the field app)"),
        el(
          "div",
          { class: "seg", role: "group", "aria-label": "I also make visits" },
          el("label", {}, yes, "Yes"),
          el("label", {}, no, "No"),
        ),
      ),
      field("Signature", signature, sigHint),
      el(
        "p",
        { class: "small muted", style: "margin:6px 0 0" },
        "The license is checked like any caregiver's; an expired one doesn't block office work.",
      ),
      el("div", { class: "actions", style: "margin-top:12px" }, saveBtn),
    ),
  ];
  const m = openModal({
    title: "My profile",
    sub: "The professional title decides what can be signed.",
    body,
  });

  async function save(close: boolean): Promise<Profile | null> {
    saveBtn.disabled = true;
    const r = await api<Profile>("PUT", "/api/me/profile", {
      name: name.value,
      mobile: mobile.value,
      email: email.value,
      title: title.value || null,
      specialty: specialty.value,
      licenseNumber: license.value,
      licenseExpires: expires.value || null,
      makesVisits: yes.checked,
      signature: signature.value,
      ...(cardId ? { licenseFileId: cardId } : {}),
    });
    saveBtn.disabled = false;
    if (!r.ok) {
      m.note.hidden = false;
      m.note.textContent = `That wasn't saved. ${r.body.error ?? ""}`.trim();
      return null;
    }
    onSaved?.(r.body.name);
    if (close) {
      m.close();
      toast("Profile saved", r.body.title ? `Professional title: ${r.body.title}` : "Office only");
    }
    return r.body;
  }
  saveBtn.addEventListener("click", () => void save(true));
  return m;
}
